import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { leadsApi, Lead, LeadCreate, LeadUpdate } from '../services/api'
import api from '../services/api'
import Modal from '../components/Modal'
import LeadForm from '../components/LeadForm'
import LeadInteractions from '../components/LeadInteractions'
import CSVImportModal from '../components/CSVImportModal'
import BulkDeleteModal from '../components/BulkDeleteModal'
import { Trash2 } from 'lucide-react'

const Leads = () => {
  const navigate = useNavigate()
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isInteractionsModalOpen, setIsInteractionsModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isTagModalOpen, setIsTagModalOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all')
  const [error, setError] = useState('')
  const [tags, setTags] = useState<any[]>([])
  const [newTag, setNewTag] = useState({ name: '', color: '#4f46e5', description: '' })
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set())
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false)
  const [companyUsers, setCompanyUsers] = useState<any[]>([])
  const [filterUser, setFilterUser] = useState<string>('all')
  const LEADS_PER_PAGE = 15

  useEffect(() => {
    loadLeads()
    loadTags()
    loadCompanyUsers()
  }, [])

  const loadCompanyUsers = async () => {
    try {
      const response = await api.get('/auth/users/company_users/')
      const users = Array.isArray(response.data) ? response.data : (response.data.results || [])
      setCompanyUsers(users)
    } catch (err) {
      console.error('Erro ao carregar usuários:', err)
    }
  }

  const loadLeads = async (ownerFilter?: string) => {
    try {
      setIsLoading(true)
      setError('')
      const params: any = { page_size: 9999 }
      const owner = ownerFilter ?? filterUser
      if (owner && owner !== 'all') params.owner = owner
      const data = await leadsApi.list(params)
      const response = data as any
      if (Array.isArray(response)) {
        setLeads(response)
      } else if (response && Array.isArray(response.results)) {
        setLeads(response.results)
      } else if (response && Array.isArray(response.leads)) {
        setLeads(response.leads)
      } else {
        setLeads([])
      }
      setCurrentPage(1)
    } catch (err: any) {
      setError('Erro ao carregar leads')
    } finally {
      setIsLoading(false)
    }
  }

  const loadTags = async () => {
    try {
      const data = await leadsApi.listTags()
      setTags(Array.isArray(data) ? data : ((data as any).results || []))
    } catch (err: any) {
      console.error('Error loading tags:', err)
    }
  }

  const handleCreateLead = async (data: LeadCreate, options?: any) => {
    try {
      setIsCreating(true)
      setError('')
      const response = await leadsApi.create(data)
      const leadId = (response as any).id
      const leadName = (response as any).name || data.name

      // Create contact + conversation if requested
      if (options?.createContact && data.phone) {
        try {
          const contactRes = await api.post('/messaging/contacts/', {
            lead: leadId,
            name: leadName,
            phone: data.phone,
            whatsapp: data.phone,
            channel: 'whatsapp',
            channel_identifier: data.phone,
            is_primary: true
          })
          if (options.startConversation && contactRes.data.id) {
            await api.post('/messaging/conversations/', {
              contact: contactRes.data.id,
              channel: 'whatsapp',
              status: 'active'
            })
            setIsModalOpen(false)
            navigate('/messaging')
            return
          }
        } catch (e) {
          console.error('Erro ao criar contato/conversa:', e)
        }
      }

      // Create deal if requested
      if (options?.createDeal?.pipelineId && options?.createDeal?.stageId) {
        try {
          await api.post('/deals/deals/', {
            lead: leadId,
            pipeline: options.createDeal.pipelineId,
            stage: options.createDeal.stageId,
            name: leadName,
            value: options.createDeal.dealValue || 0,
            status: 'open'
          })
        } catch (e) {
          console.error('Erro ao criar negociação:', e)
        }
      }

      setIsModalOpen(false)
      loadLeads()
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erro ao criar lead.'
      setError(errorMsg)
    } finally {
      setIsCreating(false)
    }
  }

  const handleStartConversation = async (lead: Lead) => {
    if (!lead.phone) {
      alert('Este lead não possui telefone cadastrado.')
      return
    }
    try {
      // Check for existing conversation
      const existingConvs = await api.get(`/messaging/conversations/?search=${lead.phone}`)
      const convList = existingConvs.data?.results || existingConvs.data || []
      if (Array.isArray(convList) && convList.length > 0) {
        navigate('/messaging')
        return
      }

      // Create contact if needed
      const contactsRes = await api.get(`/messaging/contacts/?search=${lead.phone}`)
      const contactList = contactsRes.data?.results || contactsRes.data || []
      let contactId: number

      if (Array.isArray(contactList) && contactList.length > 0) {
        contactId = contactList[0].id
      } else {
        const newContact = await api.post('/messaging/contacts/', {
          lead: lead.id,
          name: lead.name,
          phone: lead.phone,
          whatsapp: lead.phone,
          channel: 'whatsapp',
          channel_identifier: lead.phone,
          is_primary: true
        })
        contactId = newContact.data.id
      }

      await api.post('/messaging/conversations/', {
        contact: contactId,
        channel: 'whatsapp',
        status: 'active'
      })
      navigate('/messaging')
    } catch (e) {
      console.error('Erro ao iniciar conversa:', e)
    }
  }

  const handleEditLead = async (data: any) => {
    if (!selectedLead) return
    try {
      setIsCreating(true)
      setError('')
      await leadsApi.update(selectedLead.id, data)
      setIsEditModalOpen(false)
      setSelectedLead(null)
      loadLeads()
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Erro ao atualizar lead.'
      setError(errorMsg)
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteLead = async (leadId: number, leadName: string) => {
    if (!window.confirm(`Tem certeza que deseja deletar o lead "${leadName}"?`)) return
    try {
      await leadsApi.delete(leadId)
      loadLeads()
    } catch (err: any) {
      console.error('Error deleting lead:', err)
    }
  }

  const openEditModal = (lead: Lead) => {
    setSelectedLead(lead)
    setIsEditModalOpen(true)
  }

  const openInteractionsModal = (lead: Lead) => {
    setSelectedLead(lead)
    setIsInteractionsModalOpen(true)
  }

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTag.name.trim()) return
    try {
      await leadsApi.createTag(newTag)
      setNewTag({ name: '', color: '#4f46e5', description: '' })
      loadTags()
    } catch (err: any) {
      console.error('Error creating tag:', err)
    }
  }

  const handleDeleteTag = async (tagId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta tag?')) return
    try {
      await leadsApi.deleteTag(tagId)
      loadTags()
    } catch (err: any) {
      console.error('Error deleting tag:', err)
    }
  }

  const toggleSelectLead = (id: number) => {
    setSelectedLeads(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAllPage = () => {
    const pageIds = paginatedLeads.map(l => l.id)
    const allSelected = pageIds.every(id => selectedLeads.has(id))
    setSelectedLeads(prev => {
      const next = new Set(prev)
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id))
      return next
    })
  }

  const handleBulkDeleteComplete = () => {
    setIsBulkDeleteOpen(false)
    setSelectedLeads(new Set())
    loadLeads()
  }

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchTerm === '' ||
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filter === 'all' || lead.status === filter
    return matchesSearch && matchesFilter
  })

  // Pagination
  const totalPages = Math.ceil(filteredLeads.length / LEADS_PER_PAGE)
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * LEADS_PER_PAGE,
    currentPage * LEADS_PER_PAGE
  )

  // Re-fetch leads when user filter changes
  useEffect(() => {
    if (filterUser !== undefined) loadLeads(filterUser)
  }, [filterUser])

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1) }, [searchTerm, filter, filterUser])

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string, color: string, label: string }> = {
      new: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: 'Novo' },
      contacted: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', label: 'Contatado' },
      qualified: { bg: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', label: 'Qualificado' },
      proposal: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'Proposta' },
      negotiation: { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316', label: 'Negociação' },
      won: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', label: 'Ganho' },
      lost: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', label: 'Perdido' },
      archived: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', label: 'Arquivado' },
    }
    return configs[status] || { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', label: status }
  }

  const getPriorityConfig = (priority: string) => {
    const configs: Record<string, { bg: string, color: string }> = {
      low: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' },
      medium: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' },
      high: { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316' },
      urgent: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' },
    }
    return configs[priority] || { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280' }
  }

  const stats = [
    { label: 'Total', value: leads.length, icon: '📊', color: 'var(--color-primary-600)' },
    { label: 'Novos', value: leads.filter(l => l.status === 'new').length, icon: '✨', color: '#10b981' },
    { label: 'Qualificados', value: leads.filter(l => l.status === 'qualified').length, icon: '⭐', color: '#8b5cf6' },
    { label: 'Convertidos', value: leads.filter(l => l.status === 'won').length, icon: '🎯', color: '#22c55e' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', animation: 'fadeIn 0.3s ease-out' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-1)' }}>
            Gerencie todos os seus leads e oportunidades
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="glass-btn"
            style={{
              background: 'var(--glass-bg)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--space-2-5) var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Importar
          </button>
          <button
            onClick={() => setIsTagModalOpen(true)}
            className="glass-btn"
            style={{
              background: 'var(--glass-bg)',
              backdropFilter: 'var(--glass-blur)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--space-2-5) var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
              <line x1="7" y1="7" x2="7.01" y2="7"/>
            </svg>
            Tags
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              background: 'var(--bg-gradient)',
              border: 'none',
              padding: 'var(--space-2-5) var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-inverted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              boxShadow: 'var(--shadow-primary)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Novo Lead
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-4)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(239, 68, 68, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <span style={{ fontSize: 'var(--text-sm)', color: '#ef4444', fontWeight: 'var(--font-weight-medium)' }}>{error}</span>
          </div>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 'var(--space-1)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
        {stats.map((stat, index) => (
          <div
            key={index}
            style={{
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              borderRadius: 'var(--radius-2xl)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--space-5)',
              boxShadow: 'var(--shadow-glass)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>{stat.label}</p>
                <p style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--text-primary)' }}>{stat.value}</p>
              </div>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: 'var(--radius-xl)',
                background: `${stat.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--text-xl)',
              }}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-4)',
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'center',
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar leads por nome, email ou empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              width: '100%',
              padding: 'var(--space-2-5) var(--space-3) var(--space-2-5) var(--space-10)',
              fontSize: 'var(--text-sm)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-xl)',
              color: 'var(--text-primary)',
              transition: 'all var(--transition-fast)',
              outline: 'none',
            }}
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: 'var(--space-2-5) var(--space-4)',
            fontSize: 'var(--text-sm)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-xl)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            outline: 'none',
            minWidth: '180px',
          }}
        >
          <option value="all">Todos os status</option>
          <option value="new">Novos</option>
          <option value="contacted">Contatados</option>
          <option value="qualified">Qualificados</option>
          <option value="proposal">Proposta Enviada</option>
          <option value="negotiation">Negociação</option>
          <option value="won">Ganhos</option>
          <option value="lost">Perdidos</option>
          <option value="archived">Arquivados</option>
        </select>
        {companyUsers.length > 0 && (
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            style={{
              padding: 'var(--space-2-5) var(--space-4)',
              fontSize: 'var(--text-sm)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-xl)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              outline: 'none',
              minWidth: '180px',
            }}
          >
            <option value="all">Todos os atendentes</option>
            {companyUsers.map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.get_full_name || u.email}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Leads Table */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        overflow: 'hidden',
      }}>
        {isLoading ? (
          <div style={{ padding: 'var(--space-16)', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto var(--space-4)' }} />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Carregando leads...</p>
          </div>
        ) : paginatedLeads.length === 0 ? (
          <div style={{ padding: 'var(--space-16)', textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: 'var(--radius-full)', background: 'var(--color-primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-5)' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-400)" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
            </div>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              {searchTerm || filter !== 'all' ? 'Nenhum lead encontrado' : 'Nenhum lead cadastrado'}
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
              {searchTerm || filter !== 'all' ? 'Tente ajustar os filtros de busca' : 'Comece adicionando seu primeiro lead'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th style={{ padding: 'var(--space-4)', width: '44px' }}>
                    <input
                      type="checkbox"
                      checked={paginatedLeads.length > 0 && paginatedLeads.every(l => selectedLeads.has(l.id))}
                      onChange={toggleSelectAllPage}
                      style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Empresa</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Prioridade</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'left', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Origem</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Valor Est.</th>
                  <th style={{ padding: 'var(--space-4)', textAlign: 'right', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.map(lead => {
                  const statusConfig = getStatusConfig(lead.status)
                  const priorityConfig = getPriorityConfig(lead.priority)
                  return (
                    <tr key={lead.id} className="table-row" style={{ borderBottom: '1px solid var(--border-light)', transition: 'background var(--transition-fast)', background: selectedLeads.has(lead.id) ? 'rgba(79, 70, 229, 0.04)' : undefined }}>
                      <td style={{ padding: 'var(--space-4)', width: '44px' }}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.has(lead.id)}
                          onChange={() => toggleSelectLead(lead.id)}
                          style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-full)', background: 'var(--bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>
                            {lead.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: '2px' }}>{lead.name}</p>
                            {lead.email && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{lead.email}</p>}
                            {lead.tags && lead.tags.length > 0 && (
                              <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                                {lead.tags.map((tag: any) => (
                                  <span key={tag.id} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: 'var(--radius-full)', backgroundColor: tag.color, color: 'white', fontWeight: 'var(--font-weight-medium)' }}>{tag.name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{lead.company || '-'}</td>
                      <td style={{ padding: 'var(--space-4)' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', background: statusConfig.bg, color: statusConfig.color }}>{lead.status_display || statusConfig.label}</span>
                      </td>
                      <td style={{ padding: 'var(--space-4)' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', background: priorityConfig.bg, color: priorityConfig.color }}>{lead.priority_display || lead.priority}</span>
                      </td>
                      <td style={{ padding: 'var(--space-4)', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{lead.source_display || '-'}</td>
                      <td style={{ padding: 'var(--space-4)', textAlign: 'right', color: 'var(--text-primary)', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>
                        {lead.estimated_value ? `R$ ${lead.estimated_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td style={{ padding: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                          {lead.phone && (
                            <button onClick={() => handleStartConversation(lead)} className="action-btn" title="Iniciar conversa no WhatsApp" style={{ padding: 'var(--space-2)', background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', color: '#25d366', transition: 'all var(--transition-fast)' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                            </button>
                          )}
                          <button onClick={() => openInteractionsModal(lead)} className="action-btn" title="Ver interações" style={{ padding: 'var(--space-2)', background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', color: 'var(--color-primary-600)', transition: 'all var(--transition-fast)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          </button>
                          <button onClick={() => openEditModal(lead)} className="action-btn" title="Editar" style={{ padding: 'var(--space-2)', background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', color: 'var(--color-warning-600)', transition: 'all var(--transition-fast)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button onClick={() => handleDeleteLead(lead.id, lead.name)} className="action-btn" title="Deletar" style={{ padding: 'var(--space-2)', background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', color: 'var(--color-error-600)', transition: 'all var(--transition-fast)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: 'var(--space-3) var(--space-4)',
            borderTop: '1px solid var(--border-light)',
          }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
              {(currentPage - 1) * LEADS_PER_PAGE + 1}–{Math.min(currentPage * LEADS_PER_PAGE, filteredLeads.length)} de {filteredLeads.length} leads
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: 'var(--space-1-5) var(--space-3)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-primary)',
                  color: currentPage === 1 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  cursor: currentPage === 1 ? 'default' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >
                Anterior
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: 'var(--space-1-5) var(--space-2-5)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                    border: page === currentPage ? 'none' : '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)',
                    background: page === currentPage ? 'var(--bg-gradient)' : 'var(--bg-primary)',
                    color: page === currentPage ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    minWidth: '32px',
                  }}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: 'var(--space-1-5) var(--space-3)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  border: '1px solid var(--border-light)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-primary)',
                  color: currentPage === totalPages ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  cursor: currentPage === totalPages ? 'default' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                }}
              >
                Proximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Floating Bulk Action Bar */}
      {selectedLeads.size > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 'var(--space-6)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border-strong)',
          borderRadius: 'var(--radius-2xl)',
          padding: 'var(--space-3) var(--space-5)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          boxShadow: 'var(--shadow-2xl)',
          zIndex: 50,
          animation: 'slideUp 0.3s ease-out',
        }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
            {selectedLeads.size} {selectedLeads.size === 1 ? 'lead selecionado' : 'leads selecionados'}
          </span>
          <div style={{ width: '1px', height: '24px', background: 'var(--border-light)' }} />
          <button
            onClick={() => setIsBulkDeleteOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: '#ef4444',
              border: 'none',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s',
            }}
          >
            <Trash2 size={16} />
            Excluir
          </button>
          <button
            onClick={() => setSelectedLeads(new Set())}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Bulk Delete Modal */}
      <BulkDeleteModal
        isOpen={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        onConfirm={handleBulkDeleteComplete}
        type="leads"
        selectedIds={Array.from(selectedLeads)}
      />

      {/* Modals */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Novo Lead" size="lg">
        <LeadForm onSubmit={handleCreateLead} isLoading={isCreating} />
      </Modal>

      <Modal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setSelectedLead(null) }} title="Editar Lead" size="lg">
        <LeadForm lead={selectedLead} onSubmit={handleEditLead} isLoading={isCreating} />
      </Modal>

      <Modal isOpen={isInteractionsModalOpen} onClose={() => { setIsInteractionsModalOpen(false); setSelectedLead(null) }} title="Interações" size="xl">
        {selectedLead && <LeadInteractions leadId={selectedLead.id} leadName={selectedLead.name} followUpDate={selectedLead.follow_up_date || null} leadPhone={selectedLead.phone || null} leadEmail={selectedLead.email || null} onClose={() => { setIsInteractionsModalOpen(false); setSelectedLead(null) }} />}
      </Modal>

      <Modal isOpen={isTagModalOpen} onClose={() => setIsTagModalOpen(false)} title="Gerenciar Tags" size="lg">
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>Criar Nova Tag</h4>
          <form onSubmit={handleCreateTag} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)' }}>Nome *</label>
              <input type="text" value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} required placeholder="Nome da tag" style={{ width: '100%', padding: 'var(--space-2-5) var(--space-3)', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)' }}>Cor</label>
              <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} style={{ width: '48px', height: '40px', padding: '2px', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', cursor: 'pointer', background: 'var(--bg-primary)' }} />
            </div>
            <div style={{ flex: 2, minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)' }}>Descrição</label>
              <input type="text" value={newTag.description} onChange={(e) => setNewTag({ ...newTag, description: e.target.value })} placeholder="Descrição da tag" style={{ width: '100%', padding: 'var(--space-2-5) var(--space-3)', background: 'var(--bg-primary)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }} />
            </div>
            <button type="submit" style={{ padding: 'var(--space-2-5) var(--space-4)', background: 'var(--bg-gradient)', color: 'white', border: 'none', borderRadius: 'var(--radius-lg)', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', boxShadow: 'var(--shadow-primary)' }}>Criar</button>
          </form>
        </div>
        <div>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>Tags Existentes</h4>
          {tags.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>Nenhuma tag criada ainda</p>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {tags.map(tag => (
                <div key={tag.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-full)', backgroundColor: tag.color, color: 'white', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                  <span>{tag.name}</span>
                  <button onClick={() => handleDeleteTag(tag.id)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: '2px', borderRadius: 'var(--radius-full)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <CSVImportModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImportComplete={() => loadLeads()} />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        .glass-btn:hover { background: var(--glass-bg-strong) !important; border-color: var(--glass-border-strong) !important; color: var(--text-primary) !important; }
        .table-row:hover { background: var(--bg-tertiary) !important; }
        .action-btn:hover { background: var(--glass-bg) !important; transform: scale(1.05); }
        .search-input:focus { border-color: var(--color-primary-300) !important; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important; }
      `}</style>
    </div>
  )
}

export default Leads
